import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as actions from "../../../redux/actions/ProjectsActions";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { InputLabel, MenuItem, Select } from "@material-ui/core";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

const BranchSelector = ({ projectId, disabled, value: controlledValue, onChange: onChangeProp }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    branches,
    currentBranch,
    branchesNextPage,
    branchesLoading,
    branchesLoadingMore,
    branchesError,
    projectFilesContent,
  } = useSelector((state) => state.projectsReducer);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingBranch, setPendingBranch] = useState(null);
  const [selectOpen, setSelectOpen] = useState(false);
  // Ref to prevent dropdown from closing when "Load more" is clicked
  const keepOpenRef = useRef(false);

  const hasUnsavedChanges = Object.values(projectFilesContent).some(
    (f) => f.hasUnsavedChanges
  );

  const handleBranchChange = (e) => {
    const newBranch = e.target.value;

    if (newBranch === "__load_more__") {
      dispatch(actions.loadMoreBranches(projectId, branchesNextPage));
      return;
    }

    setSelectOpen(false);

    if (onChangeProp) {
      onChangeProp(newBranch);
      return;
    }

    if (newBranch === currentBranch) return;

    if (hasUnsavedChanges) {
      setPendingBranch(newBranch);
      setConfirmOpen(true);
    } else {
      switchBranch(newBranch);
    }
  };

  const handleSelectOpen = () => setSelectOpen(true);

  const handleSelectClose = () => {
    // If "Load more" was just clicked, absorb this close and keep the dropdown open
    if (keepOpenRef.current) {
      keepOpenRef.current = false;
      return;
    }
    setSelectOpen(false);
  };

  const switchBranch = (branch) => {
    dispatch(actions.setCurrentBranch(branch));
    history.replace({ search: `?branch=${branch}` });
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    if (pendingBranch) {
      switchBranch(pendingBranch);
      setPendingBranch(null);
    }
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setPendingBranch(null);
  };

  if (branchesLoading && branches.length === 0) {
    return (
      <div className="branch_selector_wrapper">
        <div className="branch_selector_loading">
          <CircularProgress size={16} />
          <span style={{ marginLeft: 8, fontSize: 12 }}>Loading branches...</span>
        </div>
      </div>
    );
  }

  if (branchesError) {
    return (
      <div className="branch_selector_wrapper">
        <div className="branch_selector_error" style={{ fontSize: 12, color: "#888", padding: "4px 8px" }}>
          Failed to load branches
        </div>
      </div>
    );
  }

  // If currentBranch is not in the loaded branches list (e.g. it's on page 2+),
  // MUI's Select won't find a matching MenuItem and will render blank. We inject
  // a hidden MenuItem so the display value always renders correctly.
  const displayValue = controlledValue !== undefined ? controlledValue : currentBranch;
  const currentBranchInList =
    !displayValue || branches.some((b) => b.name === displayValue);

  return (
    <div className="branch_selector_wrapper">
      <InputLabel shrink style={{ fontSize: 11, marginBottom: 2 }}>
        Branch
      </InputLabel>
      <Select
        value={displayValue || ""}
        onChange={handleBranchChange}
        disabled={disabled}
        open={selectOpen}
        onOpen={handleSelectOpen}
        onClose={handleSelectClose}
        fullWidth
        displayEmpty
      >
        {!currentBranchInList && (
          <MenuItem key="__current_branch__" value={displayValue} style={{ display: "none" }}>
            {displayValue}
          </MenuItem>
        )}
        {branches.map((branch) => (
          <MenuItem key={branch.name} value={branch.name}>
            {branch.name}
            {branch.default ? " (default)" : ""}
          </MenuItem>
        ))}
        {branchesLoadingMore && (
          <MenuItem disabled style={{ display: "flex", alignItems: "center", gap: 8, color: "#666" }}>
            <CircularProgress size={14} />
            <span style={{ fontSize: 12 }}>Loading...</span>
          </MenuItem>
        )}
        {branchesNextPage && !branchesLoadingMore && (
          <MenuItem
            value="__load_more__"
            onMouseDown={() => {
              // mousedown fires before MUI's click handler, which is what triggers onClose.
              // Setting the ref here ensures handleSelectClose sees it before the close fires.
              keepOpenRef.current = true;
            }}
            style={{ fontStyle: "italic", color: "#666" }}
          >
            Load more...
          </MenuItem>
        )}
      </Select>

      <Dialog open={confirmOpen} onClose={handleCancel}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          You have unsaved changes. Switching branches will discard them.
          Continue?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} color="primary" variant="contained">
            Switch Branch
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BranchSelector;
